import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from "@/composables/useNotifications";
import i18n from '@/libs/i18n';
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'

export default function useFeedbackPointList() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem, getItemsAggregate, updateItems } = useLambda()
  const userData = store.state?.userStore?.userData
  const clientId = userData.role !== "admin" ? userData.client.$oid : null
  const feedbackPoints = ref([])
  const collection = 'feedback_point'

  // Table Headers
  const tableColumns = computed(() => {
    return [
      { key: 'name', label: i18n.t('message.tableHeader.feedbackPoint'), sortable: true },
      { key: 'physical_location', label: i18n.t('message.tableHeader.physical_location'), sortable: true },
      { key: 'location', label: i18n.t('message.tableHeader.location'), sortable: true },
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
    ]
  })

  const totalFeedbackPoints = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [5, 10, 15, 20, 25, 30, 40, 50, 100]
  const searchQuery = ref('')
  const defaultSortBy = 'name'
  const sortBy = ref(defaultSortBy)
  const isSortDirDesc = ref(false)
  const isLoading = ref(true)

  const fetchFeedbackPoints = async (searchValue = '') => {
    isLoading.value = true

    try {

      const query = {
        client_id: oid(clientId),
        deleted: { $ne: true },
      }

      const pipeline = [
        { $match: query },
        {
          $lookup: {
            from: 'physical_location',
            localField: 'physical_location',
            foreignField: '_id',
            as: 'physical_location'
          }
        },
        {
          $lookup: {
            from: 'location',
            localField: 'location',
            foreignField: '_id',
            as: 'location'
          }
        },
        {
          $addFields: {
            physical_location: { $arrayElemAt: ['$physical_location.name', 0] },
            location: { $arrayElemAt: ['$location.location', 0] }
          }
        },
      ]

      const items = await getItemsAggregate({ collection, pipeline })
      if (!items) return
  
      // Search
      if (searchValue && searchValue !== searchQuery.value) return

      feedbackPoints.value  = searchQuery.value ? items.filter(e => e.name?.toLowerCase().includes(searchQuery.value.toLowerCase())) : items
      totalFeedbackPoints.value = feedbackPoints.value.length
    
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.feedbackPoint_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const searchFeedbackPoint = searchValue => {
    isLoading.value = true
    fetchFeedbackPoints(searchValue)
  }

  const deleteFeedbackPoint = async (id) => {
    try {
      const query = { _id: oid(id) }
      const action = { $set: { deleted: true } }

      await updateItem({ collection, query, action })

      fetchFeedbackPoints()
      showSuccessMessage(i18n.t('message.feedbackPoint_remove'))
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.something_went_wrong'))
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (feedbackPoints.value.length ? 1 : 0)
    const to = feedbackPoints.value.length + ((currentPage.value * perPage.value) - perPage.value)
    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalFeedbackPoints.value })
  })

  watch([currentPage, perPage], () => {
    fetchFeedbackPoints()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    fetchFeedbackPoints()
  })

  return {
    fetchFeedbackPoints,
    tableColumns,
    perPage,
    currentPage,
    perPageOptions,
    totalFeedbackPoints,
    showingMessage,
    searchQuery,
    sortBy,
    isSortDirDesc,
    feedbackPoints,
    deleteFeedbackPoint,
    isLoading,
    searchFeedbackPoint
  }
}